import React, { useState, useEffect } from "react";
import { FaTrash } from "react-icons/fa";
import TopNavbar from "../../components/Gnavbar";
import BottomNavbar from "../../components/BottomNavbar";
import { toast } from "react-toastify";
import {
  getRequest,
  postRequest,
  deleteRequest,
  postWithFormData,
} from "../../helpers/api.helper";
import { UPLOAD_URL } from "../../utils/constants";

const NavbarLogo = () => {
  const [image, setImage] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [currentLogo, setCurrentLogo] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  // ✅ Fetch the current logo on mount
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const response = await getRequest("/bureau/logo/navbar");
        if (response.success && response.data.navbarLogo) {
          setCurrentLogo(response.data.navbarLogo);
        } else {
          setCurrentLogo(null);
        }
      } catch (error) {
        console.error("Error fetching logo:", error);
        toast.error("Failed to load logo");
      } finally {
        setLoading(false);
      }
    };
    fetchLogo();
  }, []);

  // ✅ When user selects a file
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImage(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  // ✅ Upload new logo
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!image) {
      toast.warn("Please select an image first");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("image", image);
    try {
      const response = await postWithFormData("/bureau/logo/navbar", formData);
      if (response.success) {
        toast.success("Logo uploaded successfully!");
        setCurrentLogo(response.data.navbarLogo);
        setPreview(null);
      } else {
        toast.error(response.message || "Failed to upload logo");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Error uploading logo");
    } finally {
      setUploading(false);
    }
  };

  // ✅ Delete logo
  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete the current logo?"))
      return;
    try {
      const response = await deleteRequest("/bureau/logo/navbar", {});
      if (response.success) {
        toast.success("Logo deleted successfully!");
        setCurrentLogo(null);
      } else {
        toast.error(response.message || "Failed to delete logo");
      }
    } catch (error) {
      console.error("Error deleting logo:", error);
      toast.error("Error deleting logo");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex flex-col">
      <TopNavbar />

      {/* Center Section */}
      <div className="flex-grow px-4 pt-6 md:flex md:items-center md:justify-center">
        <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md pt-10">
          <h2 className="text-center text-xl font-bold text-purple-700 mb-4">
            Manage Navbar Logo
          </h2>

          {loading ? (
            <div className="text-center text-gray-500">Loading...</div>
          ) : currentLogo ? (
            <div className="mb-4 text-center relative">
              <img
                src={UPLOAD_URL + "/bureaus/navbar_logos/" + currentLogo}
                alt="Current Logo"
                className="w-full h-32 object-contain rounded bg-gray-50 border"
              />
              <button
                onClick={handleDelete}
                className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600"
              >
                <FaTrash />
              </button>
              <p className="text-sm text-gray-600 mt-2">Current Navbar Logo</p>
            </div>
          ) : (
            <div className="mb-4 text-center text-gray-500">
              No logo uploaded yet
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <label className="block text-gray-700 mb-2">Upload New Logo</label>
            <input
              type="file"
              accept="image/*"
              className="w-full border border-gray-300 rounded p-2 mb-4"
              onChange={handleImageChange}
            />

            {preview && (
              <div className="mb-4">
                <img
                  src={preview}
                  alt="Preview"
                  className="w-full h-32 object-contain rounded bg-gray-50 border"
                />
                <p className="text-center text-xs text-gray-500 mt-1">
                  Preview
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={!image || uploading}
              className={`w-full py-2 rounded ${
                image && !uploading
                  ? "bg-purple-600 text-white hover:bg-purple-700"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              {uploading ? "Uploading..." : "Upload Logo"}
            </button>
          </form>
        </div>
      </div>

      <BottomNavbar />
    </div>
  );
};

export default NavbarLogo;
